import { Injectable } from "@angular/core";
import { AccountInfo } from "@azure/msal-browser";
import { MsalService } from "@azure/msal-angular";

import { environment } from "@environments/environment";
import { AuthClaims } from "@core/auth/models/auth.model";
import { loginRequest } from "@core/auth/config/msal.config";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(private readonly msal: MsalService) {
    this.setActiveAccountIfNeeded();
  }

  login(): void {    
    if (this.msal.instance.getActiveAccount()) {
      return;
    }

    this.msal.loginRedirect(loginRequest);
  }

  logout(): void {
    this.msal.logoutRedirect({
      postLogoutRedirectUri: environment.postLogoutRedirectUri,
    });
  }

  handleRedirectObservable() {
    return this.msal.handleRedirectObservable();
  }

  getActiveAccount(): AccountInfo | null {
    return this.msal.instance.getActiveAccount();
  }

  setActiveAccountIfNeeded(): void {
    
    const active = this.msal.instance.getActiveAccount();
    if (active) {
      return;
    }

    const accounts = this.msal.instance.getAllAccounts();
    
    if (accounts.length > 0) {
      this.msal.instance.setActiveAccount(accounts[0]);
    }
  }

  isAuthenticated(): boolean {
    return this.getActiveAccount() !== null;
  }

  getClaims(): AuthClaims | null {
    const account = this.getActiveAccount();
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    if (!claims) {
      return null;
    }

    const readString = (value: unknown): string | null => (typeof value === "string" ? value : null);
    const readFirstString = (value: unknown): string | null => {
      if (typeof value === "string") {
        return value;
      }
      if (Array.isArray(value)) {
        const item = value.find((entry) => typeof entry === "string");
        return typeof item === "string" ? item : null;
      }
      return null;
    };

    return {
      userId: readString(claims["sub"]),
      tenantId: readString(claims["tenant_id"]),
      role: readFirstString(claims["role"] ?? claims["roles"]),
      name: readString(claims["name"]) ?? readString(account?.name),
      email: readFirstString(claims["email"] ?? claims["emails"]),
    };
  }

  get currentTenantId(): string | null {    
    return this.getClaims()?.tenantId ?? null;
  }

  get currentRole(): string | null {
    return this.getClaims()?.role ?? null;
  }
}